<script setup>
/**
 * PipelineAutoAssignmentSetting — Fase 4.4 frontend
 *
 * UI for the auto-assignment feature: toggle, strategy radio,
 * agent multi-select, optional max_per_agent. Reads/writes
 * `pipeline.settings['auto_assignment']` (matched by backend
 * Pipeline#auto_assignment_* helpers + Pipelines::AutoAssignmentService).
 */
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  pipeline: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});
const emit = defineEmits(['change', 'valid']);

const { t } = useI18n();
const store = useStore();

const enabled = ref(false);
const strategy = ref('round_robin');
const configuredAgentIds = ref([]);
const maxPerAgent = ref('');
const isResetting = ref(true);
let resetGeneration = 0;

const AUTO_ASSIGNMENT_STRATEGIES = new Set(['round_robin', 'workload']);

const normalizeAgentId = id => {
  if (!['number', 'string'].includes(typeof id)) return null;
  if (typeof id === 'string' && id.trim() === '') return null;
  const normalizedId = Number(id);
  return Number.isSafeInteger(normalizedId) && normalizedId > 0
    ? normalizedId
    : null;
};

const normalizeAgentIds = ids => {
  if (!Array.isArray(ids)) return [];
  return [...new Set(ids.map(normalizeAgentId).filter(id => id !== null))];
};

const normalizeStrategy = value =>
  AUTO_ASSIGNMENT_STRATEGIES.has(value) ? value : 'round_robin';

const currentAccountId = computed(() =>
  normalizeAgentId(store.getters.getCurrentAccountId)
);
const pipelineAccountId = computed(() =>
  normalizeAgentId(props.pipeline?.account_id)
);
const pipelineBelongsToCurrentAccount = computed(
  () =>
    currentAccountId.value !== null &&
    pipelineAccountId.value === currentAccountId.value
);

const allAgents = computed(() =>
  (store.getters['agents/getVerifiedAgents'] || []).filter(
    agent =>
      pipelineBelongsToCurrentAccount.value &&
      normalizeAgentId(agent?.id) !== null &&
      normalizeAgentId(agent?.account_id) === currentAccountId.value
  )
);

const availableAgentIds = computed(
  () => new Set(allAgents.value.map(agent => normalizeAgentId(agent.id)))
);

const agentIds = computed(() =>
  configuredAgentIds.value.filter(id => availableAgentIds.value.has(id))
);

const hasStaleAgentReferences = computed(
  () => configuredAgentIds.value.length !== agentIds.value.length
);

const maxPerAgentValid = computed(() => {
  if (maxPerAgent.value === '') return true;
  const value = Number(maxPerAgent.value);
  return Number.isInteger(value) && value > 0;
});

const configurationValid = computed(
  () =>
    !enabled.value ||
    (agentIds.value.length > 0 &&
      !hasStaleAgentReferences.value &&
      maxPerAgentValid.value)
);

const resetFromPipeline = async () => {
  resetGeneration += 1;
  const generation = resetGeneration;
  isResetting.value = true;
  const initial = props.pipeline?.settings?.auto_assignment || {};
  enabled.value = initial.enabled === true;
  strategy.value = normalizeStrategy(initial.strategy);
  configuredAgentIds.value = normalizeAgentIds(initial.agent_ids);
  maxPerAgent.value = initial.max_per_agent ?? '';
  await nextTick();
  if (generation !== resetGeneration) return;
  isResetting.value = false;
  emit('valid', configurationValid.value);
};

resetFromPipeline();

watch(
  [
    () => props.pipeline?.id,
    () => props.pipeline?.account_id,
    currentAccountId,
  ],
  () => resetFromPipeline(),
  { flush: 'sync' }
);

watch(
  [enabled, strategy, configuredAgentIds, maxPerAgent],
  () => {
    if (isResetting.value) return;
    emit('valid', configurationValid.value);
    if (props.readonly || !configurationValid.value) return;
    emit('change', {
      enabled: enabled.value,
      strategy: strategy.value,
      agent_ids: agentIds.value,
      max_per_agent:
        maxPerAgent.value === '' ? null : Number(maxPerAgent.value),
    });
  },
  { deep: true }
);

watch(configurationValid, valid => {
  if (!isResetting.value) emit('valid', valid);
});

const isAgentSelected = id => {
  const normalizedId = normalizeAgentId(id);
  return normalizedId !== null && agentIds.value.includes(normalizedId);
};

const toggleAgent = id => {
  if (props.readonly) return;
  const normalizedId = normalizeAgentId(id);
  if (normalizedId === null || !availableAgentIds.value.has(normalizedId)) {
    return;
  }

  const nextAgentIds = new Set(agentIds.value);
  if (nextAgentIds.has(normalizedId)) nextAgentIds.delete(normalizedId);
  else nextAgentIds.add(normalizedId);
  configuredAgentIds.value = [...nextAgentIds];
};
</script>

<template>
  <section class="space-y-3">
    <header class="flex items-center justify-between">
      <div>
        <h4 class="text-label font-semibold text-n-slate-12">
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.TITLE') }}
        </h4>
        <p class="text-label-small text-n-slate-11 mt-0.5">
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.DESCRIPTION') }}
        </p>
      </div>
      <Switch
        v-model="enabled"
        :disabled="readonly"
        :aria-label="t('PIPELINE.SETTINGS.AUTO_ASSIGN.TITLE')"
        class="shrink-0"
      />
    </header>

    <div v-if="enabled" class="pl-1 space-y-3">
      <!-- Strategy -->
      <div>
        <span
          id="pipeline-auto-assignment-strategy"
          class="text-label-small font-medium text-n-slate-11 block mb-1.5"
        >
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.STRATEGY_LABEL') }}
        </span>
        <div
          role="radiogroup"
          aria-labelledby="pipeline-auto-assignment-strategy"
          class="grid grid-cols-1 gap-2 sm:grid-cols-2"
        >
          <label
            v-for="s in ['round_robin', 'workload']"
            :key="s"
            class="min-h-11 px-3 py-2 rounded-lg border cursor-pointer transition-colors"
            :class="[
              strategy === s
                ? 'border-n-brand bg-n-brand/10 text-n-brand'
                : 'border-n-weak hover:bg-n-alpha-1 text-n-slate-12',
            ]"
          >
            <input
              v-model="strategy"
              type="radio"
              :value="s"
              :disabled="readonly"
              class="sr-only peer"
            />
            <div class="font-medium text-label">
              {{
                t(
                  `PIPELINE.SETTINGS.AUTO_ASSIGN.STRATEGY.${s.toUpperCase()}.LABEL`
                )
              }}
            </div>
            <div class="text-label-small text-n-slate-11 mt-0.5">
              {{
                t(
                  `PIPELINE.SETTINGS.AUTO_ASSIGN.STRATEGY.${s.toUpperCase()}.DESCRIPTION`
                )
              }}
            </div>
          </label>
        </div>
      </div>

      <!-- Agents -->
      <div>
        <span class="text-label-small font-medium text-n-slate-11 block mb-1.5">
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.AGENTS_LABEL') }}
          <span class="text-n-slate-10">
            {{
              t('PIPELINE.SETTINGS.AUTO_ASSIGN.SELECTED_COUNT', {
                count: agentIds.length,
              })
            }}
          </span>
        </span>
        <div
          class="max-h-40 overflow-y-auto rounded-lg border border-n-weak divide-y divide-n-weak"
        >
          <label
            v-for="agent in allAgents"
            :key="agent.id"
            class="flex min-h-11 items-center gap-2 px-3 py-2 hover:bg-n-alpha-1 cursor-pointer"
          >
            <input
              type="checkbox"
              :checked="isAgentSelected(agent.id)"
              :disabled="readonly"
              class="size-4 accent-n-brand"
              @change="toggleAgent(agent.id)"
            />
            <span class="text-label text-n-slate-12 flex-1 truncate">
              {{ agent.name }}
            </span>
            <span
              class="hidden max-w-40 truncate text-label-small text-n-slate-10 sm:block"
            >
              {{ agent.email }}
            </span>
          </label>
          <div
            v-if="!allAgents.length"
            class="px-3 py-4 text-center text-label-small text-n-slate-10"
          >
            {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.NO_AGENTS') }}
          </div>
        </div>
        <p
          v-if="enabled && (!agentIds.length || hasStaleAgentReferences)"
          role="alert"
          class="mt-1.5 text-label-small text-n-ruby-11"
        >
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.AGENTS_REQUIRED') }}
        </p>
      </div>

      <!-- Max per agent -->
      <div>
        <label
          for="pipeline-auto-assignment-max"
          class="text-label-small font-medium text-n-slate-11 block mb-1.5"
        >
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.MAX_PER_AGENT_LABEL') }}
        </label>
        <input
          id="pipeline-auto-assignment-max"
          v-model="maxPerAgent"
          name="pipelineAutoAssignmentMax"
          type="number"
          min="1"
          step="1"
          :disabled="readonly"
          :aria-invalid="!maxPerAgentValid"
          :placeholder="
            t('PIPELINE.SETTINGS.AUTO_ASSIGN.MAX_PER_AGENT_PLACEHOLDER')
          "
          class="min-h-11 w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-label text-n-slate-12 focus:outline-none focus:border-n-brand disabled:cursor-not-allowed disabled:opacity-60"
        />
        <p class="text-label-small text-n-slate-10 mt-1">
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.MAX_PER_AGENT_HINT') }}
        </p>
        <p
          v-if="!maxPerAgentValid"
          role="alert"
          class="mt-1 text-label-small text-n-ruby-11"
        >
          {{ t('PIPELINE.SETTINGS.AUTO_ASSIGN.MAX_PER_AGENT_ERROR') }}
        </p>
      </div>
    </div>
  </section>
</template>
